import React from 'react'

import i18next from 'i18next'

import ErrorBoundary403Icon from '@assets/svg/ErrorBoundary403Icon'
import Button from '@components/shared/Button/Button'
import { logger } from '@utils/logger'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  httpStatus: string | null
}

interface ErrorMessageText {
  [key: string]: {
    title: string
    subtitle: string
  }
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      httpStatus: null,
    }
  }

  static getDerivedStateFromError(error: Error) {
    logger.log('ErrorBoundary - Error caught:', error)
    logger.log('ErrorBoundary - Error name:', error.name)
    logger.log('ErrorBoundary - Error message:', error.message)
    logger.log(
      'ErrorBoundary - Full error object:',
      JSON.stringify(error, Object.getOwnPropertyNames(error))
    )

    let httpStatus = null
    const errorObj = error as any

    if (errorObj.isAxiosError && errorObj.response) {
      httpStatus = errorObj.response.status?.toString()
      logger.log('ErrorBoundary - Method 1 - HTTP Status:', httpStatus)
    }
    if (!httpStatus && error.name === 'AxiosError' && errorObj.response) {
      httpStatus = errorObj.response.status?.toString()
      logger.log('ErrorBoundary - Method 2 - HTTP Status:', httpStatus)
    }
    if (!httpStatus && error.message) {
      const statusCodeMatch = error.message.match(/status code (\d{3})/)
      if (statusCodeMatch) {
        httpStatus = statusCodeMatch[1]
        logger.log('ErrorBoundary - Method 3 - HTTP Status:', httpStatus)
      }
    }
    if (!httpStatus && error.message) {
      const httpStatusMatch = error.message.match(/(\d{3})/)
      if (httpStatusMatch) {
        httpStatus = httpStatusMatch[1]
        logger.log('ErrorBoundary - Method 4 - HTTP Status:', httpStatus)
      }
    }

    // handle HTTP status codes 403, 404, 500
    const allowedStatus = ['403', '404', '500']
    if (httpStatus && allowedStatus.includes(httpStatus)) {
      logger.log('ErrorBoundary - Final HTTP Status:', httpStatus)
      return {
        hasError: true,
        error,
        httpStatus,
      }
    }

    // ignore other errors (render children as is)
    return {
      hasError: false,
      error: null,
      httpStatus: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary - componentDidCatch:', error, errorInfo)
  }

  errorMessageText: ErrorMessageText = {
    403: {
      title: i18next.t('AccessDenied'),
      subtitle: i18next.t('AccessDeniedSubtitle'),
    },
    404: {
      title: i18next.t('PageNotFound'),
      subtitle: i18next.t('PageNotFoundSubtitle'),
    },
    500: {
      title: i18next.t('InternalServerError'),
      subtitle: i18next.t('InternalServerErrorSubtitle'),
    },

    UnexpectedError: {
      title: 'Unexpected Error',
      subtitle: 'An unexpected error occurred. Please try again.',
    },
  }

  render() {
    if (this.state.hasError) {
      logger.log('ErrorBoundary - Rendering error page')
      logger.log('ErrorBoundary - HTTP Status:', this.state.httpStatus)
      logger.log('ErrorBoundary - Error name:', this.state.error?.name)

      // if HTTP status code exists, use it, otherwise use error.name
      const errorKey =
        this.state.httpStatus || this.state.error?.name || 'UnexpectedError'
      const errorMessage =
        this.errorMessageText[errorKey as keyof ErrorMessageText] ||
        this.errorMessageText.UnexpectedError

      return (
        <div className='flex flex-col justify-center items-center -mt-24 h-screen'>
          <h1 className='text-[100px] font-bold text-blue-600'>
            {errorKey}
          </h1>
          <h1 className='text-[24px] font-semibold text-blue-600 mb-2'>
            {errorMessage.title}
          </h1>
          <p className='text-[16px] text-neutral-600 font-normal mb-6'>
            {errorMessage.subtitle}
          </p>
          <div className='my-6'>
            <ErrorBoundary403Icon />
          </div>
          <Button
            variant='primary'
            customSize='rg'
            onClick={() => {
              window.location.href = '/'
            }}
            className='mt-5 font-normal text-white rounded-md bg-blue-600'
          >
            {i18next.t('BackToProjects')}
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
